import type { SxProps, Theme } from '@mui/material/styles';
import type { ColumnMetadata } from '../grid/types';
import type { ResponsiveWidth } from './types';

const clampColumns = (columns: number): number => Math.min(12, Math.max(1, columns));

type ResponsiveBreakpoint = 'xs' | 'sm' | 'md' | 'lg' | 'xl';

type ResolvedColumnSpans = Record<ResponsiveBreakpoint, number>;

const BREAKPOINTS: ResponsiveBreakpoint[] = ['xs', 'sm', 'md', 'lg', 'xl'];

export const normalizeResponsiveWidth = (raw?: ResponsiveWidth | null): ResponsiveWidth => {
  if (!raw) {
    return { default: 12, cssClass: 'col-12' };
  }

  return {
    default: raw.default ?? 12,
    xs: raw.xs,
    sm: raw.sm,
    md: raw.md,
    lg: raw.lg,
    xl: raw.xl,
    cssClass: raw.cssClass ?? '',
  };
};

const positiveSpan = (value: number | undefined): number | undefined =>
  typeof value === 'number' && value > 0 ? value : undefined;

/**
 * Resolve effective column spans at all breakpoints (Bootstrap cascade) in one pass.
 */
const resolveAllColumnSpans = (raw?: ResponsiveWidth | null): ResolvedColumnSpans => {
  const normalized = normalizeResponsiveWidth(raw);
  const def = normalized.default ?? 12;
  const xs = positiveSpan(normalized.xs) ?? def;
  const sm = positiveSpan(normalized.sm) ?? xs;
  const md = positiveSpan(normalized.md) ?? sm;
  const lg = positiveSpan(normalized.lg) ?? md;
  const xl = positiveSpan(normalized.xl) ?? lg;
  return { xs, sm, md, lg, xl };
};

/** Convert a Bootstrap 12-column span to a percentage width. */
export const colSpanToPercent = (columns: number): string => {
  const span = clampColumns(columns);
  return `calc(${span} / 12 * 100%)`;
};

/**
 * Resolve effective column span at a breakpoint (Bootstrap cascade).
 */
export const resolveColumnSpanAtBreakpoint = (
  width: ResponsiveWidth,
  breakpoint: ResponsiveBreakpoint
): number => resolveAllColumnSpans(width)[breakpoint];

/**
 * Reversed column span (12 − caption span), matching legacy `GeRevercedWidthCss`.
 */
export const resolveReversedColumnSpanAtBreakpoint = (
  width: ResponsiveWidth,
  breakpoint: ResponsiveBreakpoint,
  reverseTo = 12
): number => {
  const span = resolveAllColumnSpans(width)[breakpoint];
  const reversed = reverseTo - span;
  return reversed > 0 ? reversed : reverseTo;
};

const mapBreakpointValues = <T>(
  spans: ResolvedColumnSpans,
  map: (span: number, breakpoint: ResponsiveBreakpoint) => T
): Record<ResponsiveBreakpoint, T> =>
  Object.fromEntries(BREAKPOINTS.map((bp) => [bp, map(spans[bp], bp)])) as Record<
    ResponsiveBreakpoint,
    T
  >;

/**
 * MUI `sx` width rules that mirror Bootstrap `col-*` classes from Back Office layout config.
 */
export const getResponsiveWidthSx = (width?: ResponsiveWidth | null): SxProps<Theme> => {
  const spans = resolveAllColumnSpans(width);

  return {
    width: mapBreakpointValues(spans, (span) => colSpanToPercent(span)),
    flexShrink: 0,
    minWidth: 0,
    boxSizing: 'border-box',
  };
};

/**
 * Row container for sibling sections — 12-column grid (matches legacy Bootstrap `.row` + `col-*`).
 */
export const DETAIL_SECTION_ROW_SX: SxProps<Theme> = {
  display: 'grid',
  gridTemplateColumns: 'repeat(12, minmax(0, 1fr))',
  columnGap: 1.5,
  rowGap: 1.5,
  width: '100%',
};

/**
 * Grid placement for a section/block inside {@link DETAIL_SECTION_ROW_SX}.
 */
export const getResponsiveGridColumnSx = (width?: ResponsiveWidth | null): SxProps<Theme> => {
  const spans = resolveAllColumnSpans(width);

  return {
    gridColumn: mapBreakpointValues(spans, (span) => `span ${span}`),
    minWidth: 0,
  };
};

/** Input area width when caption uses a partial column span. */
export const getResponsiveReversedWidthSx = (
  width?: ResponsiveWidth | null,
  reverseTo = 12
): SxProps<Theme> => {
  const spans = resolveAllColumnSpans(width);

  return {
    width: mapBreakpointValues(spans, (span) =>
      colSpanToPercent(reverseTo - span > 0 ? reverseTo - span : reverseTo)
    ),
    flex: 1,
    minWidth: 0,
    boxSizing: 'border-box',
  };
};

/** True when any breakpoint uses a partial column span (&lt; 12). */
export const hasPartialColumnSpan = (width?: ResponsiveWidth | null): boolean => {
  const spans = resolveAllColumnSpans(width);
  return BREAKPOINTS.some((bp) => spans[bp] < 12);
};

/** True when captions use a side-by-side label layout (caption span &lt; 12). */
export const usesSideBySideCaptions = (captionsWidth?: ResponsiveWidth | null): boolean =>
  hasPartialColumnSpan(captionsWidth);

/**
 * Field-level column span from BO `zFieldWidth` (`metadata.width`, 1–12).
 */
export const resolveFieldColumnSpan = (metadata: ColumnMetadata): number | null => {
  const raw = (metadata as { width?: unknown }).width;
  const parsed =
    typeof raw === 'number'
      ? raw
      : typeof raw === 'string' && raw.trim() !== ''
        ? Number.parseInt(raw, 10)
        : NaN;

  if (!Number.isInteger(parsed) || parsed <= 0 || parsed > 12) {
    return null;
  }

  return parsed;
};

export const getFieldGridColumnSx = (
  metadata: ColumnMetadata,
  options?: { forceFullWidth?: boolean }
): SxProps<Theme> => {
  if (options?.forceFullWidth) {
    return { gridColumn: 'span 12' };
  }

  const span = resolveFieldColumnSpan(metadata);
  if (span != null) {
    const clamped = clampColumns(span);
    return { gridColumn: { xs: `span ${clamped}`, sm: `span ${clamped}` } };
  }

  // Only used when BO sets an explicit column span (< 12) on the inner section grid.
  return { gridColumn: 'span 12' };
};
